import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { FollowService } from "../../services";
import { asQuery } from "../../services/common";
import { usePersistStore, useStore } from "../../store";
import { AD_STALE, DASH_STALE, AdsPage } from "./shared";

// Single-page ads from sellers the user follows (token-gated) — used by the
// home "from sellers you follow" carousel.
export function useFollowedAds(token: string, params: any) {
  return useQuery({
    queryKey: ["follows", "ads", token, params],
    queryFn: () => asQuery<AdsPage>(FollowService.getAds(token, params)),
    enabled: !!token,
    staleTime: AD_STALE,
    placeholderData: keepPreviousData,
  });
}

// Paginated ads from sellers the user follows (token-gated).
export function useFollowedAdsInfinite(token: string, params: any) {
  return useInfiniteQuery({
    queryKey: ["follows", "ads-infinite", token, params],
    queryFn: ({ pageParam }) =>
      asQuery<AdsPage>(
        FollowService.getAds(token, { ...params, page: pageParam }),
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.more ? allPages.length : undefined,
    enabled: !!token,
    staleTime: AD_STALE,
    placeholderData: keepPreviousData,
  });
}

// The sellers the user follows. Keyed under "follows" so follow/unfollow
// mutations (which invalidate ["follows"]) refresh it automatically.
export function useFollowingList(token?: string, location?: any) {
  return useQuery({
    queryKey: ["follows", "following-list", token, location],
    queryFn: () =>
      asQuery<any[]>(
        FollowService.getFollowingList(token as string, { location }),
      ),
    enabled: !!token,
    staleTime: DASH_STALE,
  });
}

export function useFollowers(token?: string, location?: any) {
  return useQuery({
    queryKey: ["follows", "followers", token, location],
    queryFn: () =>
      asQuery<any[]>(FollowService.getFollowers(token as string, { location })),
    enabled: !!token,
    staleTime: DASH_STALE,
  });
}

// Follow/unfollow own the store update (the persisted `following` array drives
// the button label) + toast, and invalidate ["follows"] so the lists/carousels
// refresh.
export function useFollowSeller() {
  const qc = useQueryClient();
  const { token, setFollowing } = usePersistStore((s: any) => s);
  const { setErrorMessage, setSuccessMessage } = useStore((s: any) => s);
  return useMutation({
    mutationFn: (sellerId: string) =>
      asQuery<string[]>(FollowService.follow(token, sellerId)),
    onSuccess: (data) => {
      setFollowing(data || []);
      setSuccessMessage("Following!");
      qc.invalidateQueries({ queryKey: ["follows"] });
    },
    onError: (e: any) => setErrorMessage(e?.message || "Request failed"),
  });
}

export function useUnfollowSeller() {
  const qc = useQueryClient();
  const { token, setFollowing } = usePersistStore((s: any) => s);
  const { setErrorMessage, setSuccessMessage } = useStore((s: any) => s);
  return useMutation({
    mutationFn: (sellerId: string) =>
      asQuery<string[]>(FollowService.unfollow(token, sellerId)),
    onSuccess: (data) => {
      setFollowing(data || []);
      setSuccessMessage("Unfollowed seller.");
      qc.invalidateQueries({ queryKey: ["follows"] });
    },
    onError: (e: any) => setErrorMessage(e?.message || "Request failed"),
  });
}
